import React, { useCallback, useEffect } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { formValueSelector } from 'redux-form';
import { useHistory } from "react-router-dom";

import Can from '../../../routes/Can';

import { showError, showConfirmation, showSuccess } from '../../../helpers/messages';

import StudentCourseSearchForm from './SearchForm';
import PageTitle from '../../../components/UI/PageTitle';
import PageContainer from '../../../components/UI/PageContainer';
import ContentContainer from '../../../components/UI/ContentContainer';
import Pagination from '../../../components/UI/Pagination';
import ExcelButton from '../../../components/UI/ExcelButton';
import Spacer from '../../../components/UI/Spacer';
import ActionButton from '../../../components/UI/ActionButton';
import SortingHeader from '../../../components/UI/SortingHeader';
import FloatingButtonAction from '../../../components/UI/FloatingButtonAction';
import PdfButton from '../../../components/UI/PdfButton';

import { 
    fetchStudentCourses, 
    downloadExcel,
    selectStudentCourse,
    deleteStudentCourse,
    downloadCertificate
} from '../../../actions/studentCourse';
import { startLoading, endLoading } from '../../../actions/ui';

const ScreensStudentCourseList = ({ nombre, apellidos, email, titulo, cursos_tipo_id }) => {

    const { t } = useTranslation();

    const dispatch = useDispatch();

    const { list, paging, filter } = useSelector(state => state.studentCourse);
    const { sort, direction } = paging;

    const history = useHistory();

    const fetchData = useCallback((options) => {
        dispatch(startLoading());
        dispatch(
            fetchStudentCourses(options)
        ).then((res) => {
            dispatch(endLoading());
            if (!res.ok) {
                showError(t('message-title-error'), t(res.message));
            }
        });
    }, [dispatch, t]);

    useEffect(() => {
        fetchData({ ...paging, ...filter });
    }, [dispatch, filter, fetchData]);

    const handlePaginate = (index) => {
        fetchData({ ...paging, ...filter, page: index });
    };

    const handleSort = (name) => {
        let newDirection = 'asc';
        if (sort === name) {
            newDirection = (direction === 'asc') ? 'desc' : 'asc';
        }
        fetchData({ ...paging, ...filter, sort: name, direction: newDirection });
    };

    const handleDelete = (id, name) => {
        showConfirmation(
            t('message-title-confirmation'),
            t('message-delete-student-course-text', { name }),
            t('button-title-delete'),
            t('button-title-cancel'),
            () => {
                dispatch(startLoading());
                dispatch(
                    deleteStudentCourse(id)
                ).then((res) => {
                    dispatch(endLoading());
                    if (res.ok) {
                        showSuccess(t('message-title-success'), t(res.message));
                        fetchData({ ...paging, ...filter, page: 1 });
                    } else {
                        showError(t('message-title-error'), t(res.message));
                    }
                });
            }
        );
    };

    const handleExcel = () => {
        dispatch(startLoading());
        dispatch(
            downloadExcel({
                nombre: nombre,
                apellidos: apellidos,
                email: email,
                titulo: titulo,
                cursos_tipo_id: cursos_tipo_id
            })
        ).then((res) => {
            dispatch(endLoading());
            if (!res.ok) {
                showError(t('message-title-error'), t(res.message));
            }
        });
    };

    const handleAdd = () => {
        history.push('/cursos/inscripciones/formulario');
    };

    const handleEdit = (item) => {
        dispatch(selectStudentCourse(item));
        history.push('/cursos/inscripciones/formulario');
    };

    const handleDownloadCertificate = (item) => {
        dispatch(downloadCertificate(item.id));
    };

    const renderTable = () => {
        return (
            <table className="dataTable striped highlight responsive-table">
                <thead>
                    <tr>
                        <SortingHeader title={ t('screens-student-course-list-apellidos') }
                            direction={ (sort === 'apellidos') ? direction : '' }
                            onClick={ () => handleSort('apellidos') }
                        />
                        <SortingHeader title={ t('screens-student-course-list-nombre') }
                            direction={ (sort === 'nombre') ? direction : '' }
                            onClick={ () => handleSort('nombre') }
                        />
                        <SortingHeader title={ t('screens-student-course-list-email') }
                            direction={ (sort === 'email') ? direction : '' }
                            onClick={ () => handleSort('email') }
                        />
                        <SortingHeader title={ t('screens-student-course-list-curso') }
                            direction={ (sort === 'titulo') ? direction : '' }
                            onClick={ () => handleSort('titulo') }
                        />
                        <SortingHeader title={ t('screens-course-list-fecha_inicio') }
                            direction={ (sort === 'fecha_inicio') ? direction : '' }
                            onClick={ () => handleSort('fecha_inicio') }
                        />
                        <SortingHeader title={ t('screens-course-list-num_unidad') }
                            direction={ (sort === 'num_unidad') ? direction : '' }
                            onClick={ () => handleSort('num_unidad') }
                        />
                        <th>{ t('screens-student-course-list-num_certificado') }</th>
                        <th>{ t('screens-list-acciones') }</th>
                    </tr>
                </thead>
                <tbody>
                    {
                        list.map((item) => (
                            <tr key={ item.id }>
                                <td>{ item.alumno.apellidos } </td>
                                <td>{ item.alumno.nombre }</td>
                                <td>{ item.alumno.email }</td>
                                <td>{ `${item.curso.titulo} (${item.curso.cursos_tipo.nombre})` }</td>
                                <td>{ item.curso.fecha_inicio }</td>
                                <td>{ item.curso.num_unidad }</td>
                                <td>{ item.num_certificado }</td>
                                <td>
                                    <Can perform="AlumnosCursos:edit" yes={() => (
                                        <ActionButton icon="edit" 
                                        tooltip={ t('screens-list-acciones-edit') }
                                        extraClass="green darken-2" 
                                        onClick={ () => handleEdit(item) } />
                                    )} data={item.curso} />
                                    {
                                        (item.num_certificado || item.curso.cursos_tipo.nombre === 'Online' || item.curso.cursos_tipo.nombre === 'Videoconferencia')
                                        ? <Can perform="AlumnosCursos:descargarCertificado" yes={() => (
                                            <PdfButton 
                                            onPressButton={ () => handleDownloadCertificate(item) } />
                                        )} />
                                        : ''
                                    }
                                    <Can perform="AlumnosCursos:delete" yes={() => (
                                        <ActionButton icon="delete_forever"
                                        tooltip={ t('screens-list-acciones-delete') } 
                                        extraClass="red ml-3" 
                                        onClick={ () => handleDelete(item.id, `${item.curso.titulo} - ${item.alumno.nombre} ${item.alumno.apellidos}`) } />
                                    )} data={item.curso} />
                                </td>
                            </tr>
                        ))
                    }
                </tbody>
            </table>
        );
    };

    return (
        <PageContainer>

            <PageTitle title={ t('screens-student-course-list-title') }>
                <Can perform="AlumnosCursos:excel" yes={() => (
                    <ExcelButton onPressButton={ handleExcel } />
                )} />
            </PageTitle>

            <StudentCourseSearchForm />

            <ContentContainer>
                { renderTable() }
            </ContentContainer>

            <Pagination paging={ paging } onPaginate={ handlePaginate } />

            <Spacer height="5" />

            <Can perform="AlumnosCursos:add" yes={() => (
                <FloatingButtonAction icon="add" 
                    title={ t('screens-list-acciones-add') } action={ handleAdd }
                />
            )} />

        </PageContainer>
    );

};

const formSelector = formValueSelector('studentCourseSearchForm');

export default connect(state => ({
    nombre: formSelector(state, 'nombre'),
    apellidos: formSelector(state, 'apellidos'),
    email: formSelector(state, 'email'),
    titulo: formSelector(state, 'titulo'),
    cursos_tipo_id: formSelector(state, 'cursos_tipo_id')
}))(ScreensStudentCourseList);